import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { SegmentExport, TSegmentExportType } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { TableColumnHeader, Time } from '@feathr/components';
import { formatBytes, TimeFormat } from '@feathr/hooks';

import ExportProgressCell from './ExportProgressCell';
import ExportStateChip from './ExportStateChip';
import optionsColumn from './optionsColumn';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

interface IRow {
  original: SegmentExport;
}

const exportTypeLabel: Record<TSegmentExportType, string> = {
  email_activity: 'Email',
  persons: 'Person',
  breadcrumb: 'Other',
};

/**
 * Column definitions for the `/data/exports` dashboard table.
 *
 * @param columnIds - When non-empty, only columns whose `id` is in the list
 *   will render. Empty array returns all columns.
 */
export default function dataExportsColumns(
  columnIds: string[],
  t: TFunction,
): Array<IColumn<SegmentExport>> {
  const columns: Array<IColumn<SegmentExport>> = [
    {
      id: 'name',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: t('Name'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      width: 220,
      sortable: false,
      Cell({ original }: IRow): JSX.Element {
        return <Observer>{(): JSX.Element => <>{original.name || '—'}</>}</Observer>;
      },
    },
    {
      id: 'state',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: t('Status'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      width: 130,
      sortable: false,
      Cell({ original }: IRow): JSX.Element {
        return <ExportStateChip segmentExport={original} />;
      },
    },
    {
      id: 'progress',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: t('Progress'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      width: 160,
      sortable: false,
      Cell({ original }: IRow): JSX.Element {
        return <ExportProgressCell segmentExport={original} />;
      },
    },
    {
      id: 'export_type',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: t('Type'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      width: 110,
      sortable: false,
      Cell({ original }: IRow): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => <>{t(exportTypeLabel[original.get('export_type')])}</>}
          </Observer>
        );
      },
    },
    {
      id: 'date_created',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: t('Created'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      width: 120,
      Cell({ original }: IRow): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => (
              <Time format={TimeFormat.shortDate} timestamp={original.dateCreated.format()} />
            )}
          </Observer>
        );
      },
    },
    {
      id: 'date_finished',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: t('Date exported'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      width: 160,
      sortable: false,
      Cell({ original }: IRow): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => {
              const finished = original.dateFinished;
              if (!finished) {
                return <>—</>;
              }
              return <Time format={TimeFormat.shortDate} timestamp={finished.format()} />;
            }}
          </Observer>
        );
      },
    },
    {
      id: 'file_size',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: t('File size'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      width: 110,
      sortable: false,
      Cell({ original }: IRow): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => {
              const fileSize = original.get('file_size');
              if (!original.dateFinished || fileSize === null || fileSize === undefined) {
                return <>—</>;
              }
              return <>{formatBytes(fileSize)}</>;
            }}
          </Observer>
        );
      },
    },
    optionsColumn(t),
  ];

  return columnIds.length
    ? columns.filter((column) => column.id !== undefined && columnIds.includes(column.id))
    : columns;
}
